import * as React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import SidePanel from 'src/components/sidePanel/SidePanel';
import { useDeleteMultipleCards } from 'src/features/cards/api/useDeleteMultipleCards';
import { useListCardsInParking } from 'src/features/cards/api/useListCardsInParking';
import { CardInParkingDetail } from 'src/features/cards/CardInParkingDetail';
import { CellCardsInParkingTableActions } from 'src/features/cards/components/CellCardsInParkingTableActions';
import { type InParkingCard } from 'src/features/cards/types/card';
import { badgeCardTypeFilterOptions, badgeCardTypeOptions } from 'src/features/cards/utils/constants';
import { cardMessages } from 'src/features/cards/utils/messages';
import { ICON_MAP } from 'src/features/parkingStatus/utils';
import { useListPoints } from 'src/features/points/api/useListPoints';
import { CustomDataTableControls } from 'src/features/statistics/components/CustomDataTableControls';
import { useListSubParkings } from 'src/features/subParkings/api/useListSubParkings';
import useFilters from 'src/hooks/useFilters';
import { useMultiRowSelection } from 'src/hooks/useMultiRowSelection';
import usePagination from 'src/hooks/usePagination';
import useSearch from 'src/hooks/useSearch';
import { useSingleRowSelection } from 'src/hooks/useSingleRowSelection';
import useSorting from 'src/hooks/useSorting';
import { useParking } from 'src/providers/ParkingProvider';
import BulkActionsDropdown from 'src/registry/components/blocks/BulkActionsDropdown';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTable, type DataTableBulkActionType } from 'src/registry/components/table';
import { CellAction } from 'src/registry/components/table/cells/CellAction';
import { CellBadge } from 'src/registry/components/table/cells/CellBadge';
import { CellCheckbox } from 'src/registry/components/table/cells/CellCheckbox';
import { CellDatetime } from 'src/registry/components/table/cells/CellDatetime';
import { CellIndex } from 'src/registry/components/table/cells/CellIndex';
import DataTableColumnGenericHeaderDefinition from 'src/registry/components/table/DataTableColumnGenericHeaderDefinition';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import { DataTablePagination } from 'src/registry/components/table/DataTablePagination';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import { toast } from 'src/registry/hooks/UseToast';
import { Badge } from 'src/registry/ui/Badge';
import { generalErrorMessages, generalMessages } from 'src/utils/messages';

const CARDS_IN_PARKING_PANEL_NAME = 'cards-in-parking-panel';

const CardsInParkingTable = () => {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();
    const { data: subParkings } = useListSubParkings({ parkingId, limit: 100 });

    const { rowSelectionState, clearSelection, setRowSelectionState } = useSingleRowSelection();
    const [pendingAction, setPendingAction] = React.useState<DataTableBulkActionType | null>(null);

    const {
        rowSelectionState: multiRowState,
        setRowSelectionState: setMultiRowState,
        selectedIds,
    } = useMultiRowSelection();

    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'enter_datetime', desc: true }],
    });
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'card_number' });

    const { data, isLoading, isError, refetch } = useListCardsInParking({
        ...querySortingOptions,
        ...queryPaginationOptions,
        ...queryFiltersOptions,
        ...querySearchOptions,
        parkingId,
    });

    const { data: points } = useListPoints({ parkingId });

    const { mutate: deleteMultipleCards } = useDeleteMultipleCards({
        onSuccess: (_) => {
            void refetch();
            toast({
                title: formatMessage(cardMessages.cardsDeletedSuccessfully),
                description: formatMessage(cardMessages.cardsDeletedSuccessfullyDetails),
            });
        },
        onError: () => {
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const singleRowSelection = React.useCallback((rowId: string) => {
        setRowSelectionState({ [rowId]: true });
    }, []);

    const columnHelper = createColumnHelper<InParkingCard>();

    const columns = [
        columnHelper.display({
            id: 'select',
            cell: (ctx) => <CellCheckbox {...ctx} />,
            enableSorting: false,
            enableHiding: false,
            maxSize: 45,
        }),
        columnHelper.display({
            header: DataTableColumnGenericHeaderDefinition,
            id: 'id',
            cell: CellIndex,
            maxSize: 45,
            meta: { title: '#' },
        }),
        columnHelper.accessor('plate_number', {
            header: DataTableColumnHeader,
            cell: (ctx) => (
                <CellAction
                    {...ctx}
                    sidePanelName={CARDS_IN_PARKING_PANEL_NAME}
                    singleRowSelection={singleRowSelection}
                    render={() => (
                        <div className="flex flex-col">
                            <span>{ctx.getValue()}</span>
                            <div className="flex w-full flex-wrap gap-x-2">
                                {ctx.row.original.tags?.map((tag) => (
                                    <Badge key={tag.id} theme={tag.card_tag.theme} className="mt-1">
                                        {tag.card_tag.text}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    )}
                />
            ),
            enableSorting: true,
            meta: {
                title: formatMessage(cardMessages.cardNumber),
            },
        }),
        columnHelper.accessor('card_number', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            meta: {
                title: formatMessage(cardMessages.ticket),
            },
        }),
        columnHelper.accessor('parking_name', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            meta: {
                inputType: INPUT_TYPE.MULTI_SELECT,
                title: formatMessage(cardMessages.parkingZone),
                options: subParkings?.results?.map((zone) => ({ value: zone.id, label: zone.name })),
            },
            id: 'parking',
        }),
        columnHelper.accessor('owner_name', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            meta: {
                title: formatMessage(cardMessages.ownerName),
            },
        }),
        columnHelper.accessor('card_type', {
            header: DataTableColumnHeader,
            cell: (props) => <CellBadge badgeOptions={badgeCardTypeOptions} {...props} />,
            enableSorting: true,
            enableHiding: true,
            enableColumnFilter: true,
            meta: {
                inputType: INPUT_TYPE.MULTI_SELECT,
                title: formatMessage(cardMessages.cardType),
                badgeOptions: badgeCardTypeFilterOptions,
            },
        }),
        columnHelper.accessor('enter_datetime', {
            header: DataTableColumnHeader,
            cell: CellDatetime,
            enableSorting: true,
            enableHiding: true,
            meta: {
                title: formatMessage(cardMessages.enterDatetime),
            },
        }),
        columnHelper.accessor('entry_point', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            enableColumnFilter: true,
            meta: {
                inputType: INPUT_TYPE.MULTI_SELECT,
                title: formatMessage(cardMessages.entryPoint),
                options: points?.results.map((point) => ({ value: point.name, label: point.name })),
            },
        }),
        columnHelper.accessor('charge_plan_name', {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(cardMessages.chargePlan),
            },
        }),
        columnHelper.display({
            header: DataTableColumnGenericHeaderDefinition,
            id: 'actions',
            cell: (props) => (
                <CellCardsInParkingTableActions
                    {...props}
                    sidePanelName={CARDS_IN_PARKING_PANEL_NAME}
                    singleRowSelection={singleRowSelection}
                />
            ),
        }),
        columnHelper.accessor('enter_datetime', {
            header: DataTableColumnHeader,
            enableColumnFilter: true,
            cell: () => null,
            meta: {
                inputType: INPUT_TYPE.SINGLE_DATE,
                filterLabel: formatMessage(cardMessages.entryFrom),
                customDropdownMenuLabel: formatMessage(cardMessages.filterByLabel, {
                    value: `"${formatMessage(cardMessages.entryFrom)}"`,
                }),
            },
            id: 'date_greater_than',
        }),
        columnHelper.accessor('enter_datetime', {
            header: DataTableColumnHeader,
            enableColumnFilter: true,
            cell: () => null,
            meta: {
                inputType: INPUT_TYPE.SINGLE_DATE,
                filterLabel: formatMessage(cardMessages.entryTo),
                customDropdownMenuLabel: formatMessage(cardMessages.filterByLabel, {
                    value: `"${formatMessage(cardMessages.entryTo)}"`,
                }),
            },
            id: 'date_less_than',
        }),
    ];

    const bulkActions = [
        {
            label: formatMessage(generalMessages.delete),
            id: 'delete',
            icon: ICON_MAP.DELETE,
            onSelect: () => deleteMultipleCards({ parkingId, cardsId: selectedIds }),
        },
    ];

    const DataTableControls = React.useMemo(
        () =>
            CustomDataTableControls(
                <>
                    {bulkActions && (
                        <BulkActionsDropdown
                            bulkActions={bulkActions}
                            selectedIds={selectedIds}
                            setPendingAction={setPendingAction}
                        />
                    )}
                    {pendingAction && pendingAction.id === 'delete' && (
                        <ConfirmationModal
                            isModalOpen={!!pendingAction}
                            setIsModalOpen={(open) => !open && setPendingAction(null)}
                            dialogDetails={{
                                title: { ...generalMessages.delete },
                                subTitle: { ...cardMessages.deleteSelectedEntries },
                                icon: ICON_MAP.DELETE,
                                confirmationMessage: {
                                    ...cardMessages.deleteSelectedEntriesConfirmation,
                                    values: { count: selectedIds.length },
                                },
                            }}
                            callbackButton={{
                                variant: 'destructiveLight',
                                buttonTitle: generalMessages.delete,
                                callbackFn: () => {
                                    if (pendingAction) {
                                        pendingAction.onSelect();
                                        setMultiRowState({});
                                        setPendingAction(null);
                                    }
                                },
                            }}
                        />
                    )}
                </>,
            ),
        [selectedIds, setPendingAction, pendingAction],
    );

    // Calculate the selected row data
    const getRowData = React.useMemo(() => {
        const firstSelected = Object.keys(rowSelectionState)[0];
        if (!data || !firstSelected) return;
        return data.results.find((row) => row.id === parseInt(firstSelected));
    }, [data, rowSelectionState]);

    return (
        <>
            <DataTable
                columns={columns}
                data={data}
                pagination={pagination}
                sorting={sorting}
                filters={filters}
                search={search}
                semantics={{
                    title: formatMessage(cardMessages.vehiclesInParking),
                    entryName: formatMessage(cardMessages.vehicle),
                    entryNames: formatMessage(cardMessages.vehicles),
                    placeholder: formatMessage(cardMessages.cardNumber),
                    sidePanelName: CARDS_IN_PARKING_PANEL_NAME,
                    initialColumnVisibility: { date_greater_than: false, date_less_than: false },
                }}
                isError={isError}
                isLoading={isLoading}
                rowSelection={{
                    rowSelectionState: multiRowState,
                    setRowSelectionState: setMultiRowState,
                }}
                isMultiSelect
                getRowId={(row) => row.id.toString()}
                components={{
                    controls: DataTableControls,
                    pagination: DataTablePagination,
                }}
                enableLocalFilters={true}
            />

            <SidePanel onClose={clearSelection} name={CARDS_IN_PARKING_PANEL_NAME}>
                <CardInParkingDetail inParkingCard={getRowData} refetch={refetch} />
            </SidePanel>
        </>
    );
};

export default CardsInParkingTable;
